import {
  FOLDER_ROOT_PATH,
  FolderPath,
  folderPathDepth,
  folderPathName,
  isSameOrDescendantFolderPath,
} from '@lib/library/folderPath';
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined';
import VerticalAlignTopRounded from '@mui/icons-material/VerticalAlignTopRounded';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { LibraryFolderStore, MediaMoveSource } from '@stores/Library/LibraryFolder';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { FolderDialogShell } from './FolderDialogShell';
import css from './FolderDialogs.module.scss';
import { FolderEditDialog } from './FolderEditDialog';
import {
  folderLocation,
  FolderSearchField,
  folderTreeCss,
  FolderTreeLabel,
  FolderTreeRow,
  searchFolders,
  useFolderTreeExpansion,
} from './FolderTreeView';

export type FolderPickerPurpose =
  | { kind: 'media'; mediaIds: string[]; via: Exclude<MediaMoveSource, 'drag'> }
  | { kind: 'folder'; path: FolderPath };

/**
 * Picks where media or a folder should go: the same tree as the "All folders" finder, with the
 * same search. Places it can't go are shown but disabled — the folder it is already in, and for a
 * folder, itself and everything under it — so the tree keeps its shape and the user can see why.
 *
 * The library's own row is gone, as in the finder, but the library's top level is still a
 * destination — the way out of every folder — so it gets a "Top level" row, shown only while it is
 * one: not for media already there, which is where most media sit.
 */
export const FolderPickerDialog = observer(function FolderPickerDialog({
  folder,
  purpose,
  onClose,
  onPick,
}: {
  folder: LibraryFolderStore;
  purpose: FolderPickerPurpose;
  onClose: () => void;
  onPick: (destination: FolderPath) => Promise<void>;
}) {
  const [selected, setSelected] = useState<FolderPath | null>(null);
  const [creatingIn, setCreatingIn] = useState<FolderPath | null>(null);
  const [query, setQuery] = useState('');

  const currentHome = (() => {
    if (purpose.kind === 'folder') {
      return folder.foldersByPath.get(purpose.path)?.parent ?? FOLDER_ROOT_PATH;
    }
    const moving = new Set(purpose.mediaIds);
    const homes = new Set(
      folder.allFiles.filter((file) => moving.has(file._id)).map((file) => folder.folderOf(file)),
    );
    return homes.size === 1 ? [...homes][0] : null;
  })();

  const tree = useFolderTreeExpansion(folder, currentHome ?? folder.currentFolderPath);

  const unavailable = (path: FolderPath) =>
    path === currentHome ||
    (purpose.kind === 'folder' && isSameOrDescendantFolderPath(path, purpose.path));

  const needle = query.trim().toLowerCase();
  const rows = needle
    ? searchFolders(folder, needle)
    : searchFolders(folder, '').filter((row) => tree.isVisible(row.path));
  const showTopLevel = !needle && !unavailable(FOLDER_ROOT_PATH);

  const selectFirst = () => {
    const first = rows.find((row) => !unavailable(row.path));
    if (first) setSelected(first.path);
  };

  const count = purpose.kind === 'media' ? purpose.mediaIds.length : 1;
  const title =
    purpose.kind === 'media'
      ? `Move ${count === 1 ? 'media' : `${count} media`} to…`
      : 'Move folder to…';

  // A new folder goes inside the one picked, and the tooltip says where.
  const newFolderParent = selected ?? FOLDER_ROOT_PATH;
  const newFolderButton = (
    <Tooltip
      title={
        newFolderParent === FOLDER_ROOT_PATH
          ? 'Create a folder at the top level'
          : `Create a folder inside “${folderPathName(newFolderParent)}”`
      }
      placement="top"
      arrow
    >
      <span>
        <ActionButton
          variant="secondary"
          type="button"
          className={css.newFolderButton}
          onClick={() => setCreatingIn(newFolderParent)}
        >
          <CreateNewFolderOutlined fontSize="small" />
          New folder
        </ActionButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      <FolderDialogShell
        modalName="folder-picker"
        open
        onClose={onClose}
        title={title}
        confirmLabel="Move here"
        loadingText="Moving…"
        confirmDisabled={selected === null}
        onConfirm={async () => {
          if (selected === null) return;
          await onPick(selected);
        }}
        leadingAction={newFolderButton}
      >
        <FolderSearchField value={query} onChange={setQuery} onSubmitFirst={selectFirst} />
        <div className={css.tree} role="tree" aria-label="Folders">
          {showTopLevel && (
            <FolderTreeRow
              depth={0}
              expandable={false}
              expanded={false}
              onToggle={() => {}}
              name="Top level"
              className={classNames(selected === FOLDER_ROOT_PATH && folderTreeCss.selected)}
            >
              <button
                type="button"
                aria-pressed={selected === FOLDER_ROOT_PATH}
                className={folderTreeCss.rowMain}
                onClick={() => setSelected(FOLDER_ROOT_PATH)}
              >
                <FolderTreeLabel
                  icon={<VerticalAlignTopRounded fontSize="small" />}
                  name="Top level"
                  subtitle="Out of every folder"
                />
              </button>
            </FolderTreeRow>
          )}
          {rows.map(({ path, entry }) => {
            const disabled = unavailable(path);
            return (
              <FolderTreeRow
                key={path}
                depth={needle ? 0 : folderPathDepth(path)}
                color={entry.color}
                expandable={!needle && tree.hasChildren(path)}
                expanded={tree.isExpanded(path)}
                onToggle={() => tree.toggle(path)}
                name={entry.name}
                className={classNames(
                  selected === path && folderTreeCss.selected,
                  disabled && folderTreeCss.unavailable,
                )}
              >
                <button
                  type="button"
                  aria-pressed={selected === path}
                  disabled={disabled}
                  className={folderTreeCss.rowMain}
                  onClick={() => setSelected(path)}
                >
                  <FolderTreeLabel
                    entry={entry}
                    name={entry.name}
                    needle={needle}
                    subtitle={needle && folderLocation(folder, path)}
                    trailing={
                      path === currentHome && <span className={folderTreeCss.note}>Current</span>
                    }
                  />
                </button>
              </FolderTreeRow>
            );
          })}
          {rows.length === 0 && (
            <span className={css.treeEmpty}>
              {needle ? `No folder named “${query.trim()}”` : 'No folders yet — create one below.'}
            </span>
          )}
        </div>
      </FolderDialogShell>
      {creatingIn !== null && (
        <FolderEditDialog
          folder={folder}
          target={{ kind: 'create', parent: creatingIn }}
          onClose={() => setCreatingIn(null)}
          onSaved={(path) => {
            setQuery('');
            tree.show(path);
            setSelected(path);
          }}
        />
      )}
    </>
  );
});
